import React, { useCallback, useMemo } from 'react';
import { useTokenTranslate } from '../../contexts/token_translation_context';
import styled from 'styled-components';
import { formatPrice } from '@lemonadefashion/money';
import { useHistory } from 'react-router-dom';
import { useWallet } from '../../contexts/wallet';
import { CreditSummaryLine } from './history_row';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const BalanceSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const BalanceLabel = styled.span`
  font-size: 13px;
  color: #888;
  margin-bottom: 4px;
`;

const BalanceAmount = styled.span`
  font-size: 56px;
  font-weight: 700;
  color: #2a1a6e;
  line-height: 1.1;
`;

const ShopButton = styled.button`
  background: #6A7BFF;
  color: #fff;
  border: none;
  border-radius: 24px;
  height: 43px;
  width: 100%;
  max-width: 271px;
  align-self: center;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin: 4px auto 6px;
  display: block;
  &:hover { background: #5567ee; }
`;

const Note = styled.p`
  font-size: 12px;
  color: #aaa;
  text-align: center;
  margin: 0 0 14px;
`;

const Divider = styled.div`
  border-bottom: 1px dashed #ddd;
  margin: 0 0 12px;
`;

const SummaryHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 6px;
`;

const SummaryTitle = styled.h4`
  font-size: 20px;
  font-weight: 600;
  color: #666;
  margin: 0;
`;

const SeeAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: #6A7BFF;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { text-decoration: underline; }
`;

const SummaryList = styled.div`
  overflow-y: auto;
  max-height: 90px;
  padding-right: 8px;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: #aaa;
  margin: 8px 0;
`;

const ArrowRight = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' fill='none'>
    <path d='M5 12h14M13 5l7 7-7 7' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const PippCreditContent = ({ userName, credits, onCheckAllCredits }) => {
  const history = useHistory();
  const { closeWallet, currency } = useWallet();
  const { translate, locale } = useTokenTranslate();
  const filtered = useMemo(
    () => credits
      ? credits.filter(c => c && c.actual_reward.amount !== 0 && c.actual_reward.currency === currency)
      : [],
    [credits, currency]
  );
  const balance = useMemo(
    () => filtered.reduce((sum, c) => sum + c.actual_reward.amount, 0),
    [filtered]
  );

  const handleShop = useCallback(() => {
    closeWallet();
    history.push('/shop/all');
  }, [closeWallet, history]);

  return (
    <Wrap>
      <BalanceSection>
        <BalanceLabel>{translate('web.pipp_credit_content.in_app_credit_text')}</BalanceLabel>
        <BalanceAmount>{formatPrice({ amount: balance, currency })}</BalanceAmount>
        <ShopButton onClick={handleShop}>{translate('web.pipp_credit_content.shop_button')}</ShopButton>
        <Note>{translate('web.pipp_credit_content.in_app_credit_non_withdrawable_text')}</Note>
      </BalanceSection>

      <Divider />

      <SummaryHead>
        <SummaryTitle>
          {locale.startsWith('ar')
            ? `${userName} ${translate('web.pipp_credit_content.credits_text')}`
            : `${userName}'s ${translate('web.pipp_credit_content.credits_text')}`}
        </SummaryTitle>
        {filtered.length > 0 && (
          <SeeAllLink onClick={onCheckAllCredits}>
            {translate('web.pipp_credit_content.check_all_credits_button')} <ArrowRight />
          </SeeAllLink>
        )}
      </SummaryHead>

      <SummaryList>
        {filtered.length > 0
          ? filtered.map(credit => (
            <CreditSummaryLine key={credit.id}
              credit={credit} />
          ))
          : <EmptyText>{translate('web.pipp_credit_content.no_credits_text')}</EmptyText>}
      </SummaryList>
    </Wrap>
  );
};

export default PippCreditContent;
